import React, { useState, useEffect, useMemo } from "react";
import { Table, Input, Select, Button, notification, Spin, Card, Row, Col, Typography, Dropdown, Space, Pagination } from "antd";
import { SearchOutlined, ReloadOutlined, DownloadOutlined, FileTextOutlined, FilePdfOutlined, FileExcelOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import APICall from "../../../APICall";
import EndPoints from "../../../APICall/EndPoints";
// import { useSelector } from "react-redux";
import useDebounce from "../../../hooks/3-useDebounce/useDebounce";
import { SEGMENTS } from "../../../Helper/constant";
import { pdf, Document, Page, Text as PDFText, View as PDFView, StyleSheet } from "@react-pdf/renderer";

const { Title, Text } = Typography;
const { Option } = Select;

const ScriptInfo = () => {
  // State
  const [searchValue, setSearchValue] = useState("");
  const [selectedMarket, setSelectedMarket] = useState(SEGMENTS["NSE EQU"]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortConfig, setSortConfig] = useState({ field: null, order: null });

  // Redux
  // const { userDetail } = useSelector((state) => state.authSlice);

  // Debounced search value
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Available markets - using the SEGMENTS constant for consistency
  const markets = [
    { value: SEGMENTS["NSE EQU"], label: "NSE EQUITY" },
    { value: SEGMENTS["NSE FUT"], label: "NSE FUTURES" },
    { value: SEGMENTS["NSE OPT"], label: "NSE OPTIONS" },
    { value: SEGMENTS["MCX"], label: "MCX FUTURES" },
    { value: SEGMENTS["MCX OPT"], label: "MCX OPTIONS" },
    { value: SEGMENTS["FOREX"], label: "FOREX" },
    { value: SEGMENTS["COMEX"], label: "COMEX" },
    { value: SEGMENTS["GLOBEX"], label: "GLOBEX" },
    { value: SEGMENTS["CRYPTO"], label: "CRYPTO" },
  ];

  // Fetch script data
  const {
    data: scriptData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["scriptInfo", selectedMarket],
    queryFn: async () => {
      try {
        const response = await APICall("get", `${EndPoints.getTableScript}${selectedMarket}`);
        if (response?.status === 200) {
          const scriptsData = response?.data?.data?.data || [];
          console.log("📊 Script data received:", {
            market: selectedMarket,
            totalScripts: scriptsData.length,
            sampleScript: scriptsData[0],
            dataStructure: scriptsData.slice(0, 3)
          });
          return scriptsData;
        }
        throw new Error(response?.data?.message || "Failed to fetch script data");
      } catch (error) {
        throw new Error(error?.message || "Network error");
      }
    },
    enabled: !!selectedMarket,
    refetchOnWindowFocus: false,
  });

  // Process and filter data
  const processedData = useMemo(() => {
    if (!scriptData || !Array.isArray(scriptData)) {
      console.log("⚠️ No script data available:", { scriptData });
      return [];
    }

    console.log("🔄 Processing script data:", {
      totalScripts: scriptData.length,
      firstScript: scriptData[0],
      searchValue: debouncedSearch,
      uniqueScripts: new Set(scriptData.map(s => s.symbolName || s.symbol)).size
    });

    let filtered = scriptData.filter((script) => {
      if (!debouncedSearch) return true;
      const searchLower = debouncedSearch.toLowerCase();
      return (
        script.symbolName?.toLowerCase().includes(searchLower) ||
        script.symbol?.toLowerCase().includes(searchLower) ||
        script.scriptType?.toLowerCase().includes(searchLower)
      );
    });

    // Apply sorting
    if (sortConfig.field && sortConfig.order) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.field];
        let bVal = b[sortConfig.field];

        if (typeof aVal === "number" && typeof bVal === "number") {
          return sortConfig.order === "asc" ? aVal - bVal : bVal - aVal;
        }

        aVal = String(aVal || "").toLowerCase();
        bVal = String(bVal || "").toLowerCase();
        return sortConfig.order === "asc" 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      });
    }

    // Add unique identifiers to each row to prevent duplication issues
    const processedWithIds = filtered.map((script, index) => ({
      ...script,
      _uniqueId: `${selectedMarket}-${script.symbolName || script.symbol || index}`,
      _rowIndex: index
    }));

    return processedWithIds;
  }, [scriptData, debouncedSearch, sortConfig, selectedMarket]);

  // Pagination
  const paginatedData = useMemo(() => {
    if (!processedData || !Array.isArray(processedData)) {
      console.log("⚠️ No processed data for pagination");
      return [];
    }
    
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const pageData = processedData.slice(startIndex, endIndex);
    
    console.log("📄 Pagination data:", {
      currentPage,
      pageSize,
      startIndex,
      endIndex,
      totalData: processedData.length,
      pageDataLength: pageData.length,
      samplePageData: pageData[0],
      uniqueKeys: new Set(pageData.map(r => r._uniqueId)).size,
      allKeys: pageData.map(r => r._uniqueId),
      paginationInfo: {
        totalPages: Math.ceil(processedData.length / pageSize),
        currentPage,
        pageSize,
        totalItems: processedData.length
      },
      debug: {
        hasData: processedData.length > 0,
        validPage: currentPage > 0,
        validPageSize: pageSize > 0,
        startIndexValid: startIndex >= 0,
        endIndexValid: endIndex > startIndex
      }
    });
    
    return pageData;
  }, [processedData, currentPage, pageSize]);

  // Table columns - simplified to show only requested fields
  const columns = [
    {
      title: "Sr No",
      dataIndex: "srNo",
      key: "srNo",
      width: 80,
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Script Name",
      dataIndex: "symbolName",
      key: "symbolName",
      width: 300,
      sorter: true,
      render: (text) => (
        <Text strong style={{ color: "#1890ff" }}>
          {text || "N/A"}
        </Text>
      ),
    },
    {
      title: "Close",
      dataIndex: "open",
      key: "open",
      width: 150,
      sorter: true,
      render: (value) => {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return <Text style={{ color: "#666" }}>N/A</Text>;
        
        return (
          <Text strong style={{ color: numValue > 0 ? "#52c41a" : "#ff4d4f" }}>
            {numValue.toFixed(2)}
          </Text>
        );
      },
    },
    {
      title: "Lot Size",
      dataIndex: "lotSize",
      key: "lotSize",
      width: 150,
      sorter: true,
      render: (value) => {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return <Text style={{ color: "#666" }}>N/A</Text>;
        
        return (
          <Text style={{ color: "#666" }}>
            {numValue.toLocaleString()}
          </Text>
        );
      },
    },
  ];

  // Handle table change (sorting, pagination)
  // const handleTableChange = (pagination, filters, sorter) => {
  //   if (sorter.field !== sortConfig.field || sorter.order !== sortConfig.order) {
  //     setSortConfig({
  //       field: sorter.field,
  //       order: sorter.order === "ascend" ? "asc" : sorter.order === "descend" ? "desc" : null,
  //     });
  //     setCurrentPage(1);
  //   }
  // };

  // Handle search
  const handleSearch = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(1);
  };

  // Handle market change
  const handleMarketChange = (value) => {
    setSelectedMarket(value);
    setCurrentPage(1);
    setSearchValue("");
    setSortConfig({ field: null, order: null });
    
    // Clear any cached data to prevent duplication
    console.log("🔄 Market changed to:", value);
  };

  // Handle refresh
  const handleRefresh = () => {
    refetch();
    setCurrentPage(1);
    setSearchValue("");
    setSortConfig({ field: null, order: null });
  };

  // Export functions
  const exportToCSV = () => {
    if (!processedData || processedData.length === 0) {
      notification.warning({ message: "No data to export" });
      return;
    }

    const headers = ["Sr No", "Script Name", "Close", "Lot Size"];
    const csvData = processedData.map((item, index) => [
      index + 1,
      item.symbolName || item.symbol || "N/A",
      item.open ? parseFloat(item.open).toFixed(2) : "N/A",
      item.lotSize || "N/A"
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `script-info-${selectedMarket}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    notification.success({ message: "CSV exported successfully" });
  };

  const exportToJSON = () => {
    if (!processedData || processedData.length === 0) {
      notification.warning({ message: "No data to export" });
      return;
    }

    const jsonData = processedData.map((item, index) => ({
      srNo: index + 1,
      scriptName: item.symbolName || item.symbol || "N/A",
      close: item.open ? parseFloat(item.open).toFixed(2) : "N/A",
      lotSize: item.lotSize || "N/A"
    }));

    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `script-info-${selectedMarket}-${new Date().toISOString().split('T')[0]}.json`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    notification.success({ message: "JSON exported successfully" });
  };

  const exportToPDF = async () => {
    if (!processedData || processedData.length === 0) {
      notification.warning({ message: "No data to export" });
      return;
    }

    // Show loading notification
    const loadingKey = 'pdf-export';
    notification.info({
      key: loadingKey,
      message: 'Generating PDF...',
      description: `Processing ${processedData.length} scripts...`,
      duration: 0,
    });

    try {
      // Process data in chunks to prevent UI freezing
      const chunkSize = 100;
      const chunks = [];
      for (let i = 0; i < processedData.length; i += chunkSize) {
        chunks.push(processedData.slice(i, i + chunkSize));
      }

      // Create styles once
      const styles = StyleSheet.create({
        page: {
          paddingTop: 24,
          paddingBottom: 24,
          paddingHorizontal: 24,
          fontSize: 10,
        },
        table: {
          display: "table",
          width: "auto",
          borderStyle: "solid",
          borderWidth: 1,
          borderRightWidth: 0,
          borderBottomWidth: 0,
        },
        row: {
          flexDirection: "row",
        },
        headerCell: {
          flexGrow: 1,
          borderStyle: "solid",
          borderWidth: 1,
          borderLeftWidth: 0,
          borderTopWidth: 0,
          backgroundColor: "#f2f2f2",
          fontWeight: 700,
          padding: 6,
        },
        cell: {
          flexGrow: 1,
          borderStyle: "solid",
          borderWidth: 1,
          borderLeftWidth: 0,
          borderTopWidth: 0,
          padding: 6,
        },
        colSr: { width: 48 },
        colName: { width: 280 },
        colClose: { width: 100, textAlign: "right" },
        colLot: { width: 80, textAlign: "right" },
      });

      // Process chunks with small delays to prevent UI freezing
      const processChunk = async (chunk, chunkIndex) => {
        // Update progress
        notification.info({
          key: loadingKey,
          message: 'Generating PDF...',
          description: `Processing chunk ${chunkIndex + 1}/${chunks.length} (${chunkIndex * chunkSize + chunk.length} of ${processedData.length} scripts)`,
          duration: 0,
        });

        // Small delay to prevent UI freezing
        await new Promise(resolve => setTimeout(resolve, 10));

        return chunk.map((item, index) => ({
          srNo: chunkIndex * chunkSize + index + 1,
          scriptName: item.symbolName || item.symbol || "N/A",
          close: item.open ? Number.parseFloat(item.open) : null,
          lotSize: item.lotSize ?? null,
        }));
      };

      // Process all chunks
      const allTableData = [];
      for (let i = 0; i < chunks.length; i++) {
        const chunkData = await processChunk(chunks[i], i);
        allTableData.push(...chunkData);
      }

      // Create PDF document
      const Doc = (
        <Document>
          <Page size="A4" style={styles.page} wrap>
            <PDFView style={styles.table}>
              <PDFView style={styles.row}>
                <PDFText style={[styles.headerCell, styles.colSr]}>Sr No</PDFText>
                <PDFText style={[styles.headerCell, styles.colName]}>Script Name</PDFText>
                <PDFText style={[styles.headerCell, styles.colClose]}>Close</PDFText>
                <PDFText style={[styles.headerCell, styles.colLot]}>Lot Size</PDFText>
              </PDFView>
              {allTableData.map((row) => (
                <PDFView key={row.srNo} style={styles.row}>
                  <PDFText style={[styles.cell, styles.colSr]}>{row.srNo}</PDFText>
                  <PDFText style={[styles.cell, styles.colName]}>{row.scriptName}</PDFText>
                  <PDFText style={[styles.cell, styles.colClose]}>
                    {row.close == null || Number.isNaN(row.close) ? "N/A" : row.close.toFixed(2)}
                  </PDFText>
                  <PDFText style={[styles.cell, styles.colLot]}>
                    {row.lotSize == null || Number.isNaN(Number(row.lotSize)) ? "N/A" : Number(row.lotSize).toLocaleString()}
                  </PDFText>
                </PDFView>
              ))}
            </PDFView>
          </Page>
        </Document>
      );

      // Generate PDF with progress update
      notification.info({
        key: loadingKey,
        message: 'Generating PDF...',
        description: 'Creating PDF document...',
        duration: 0,
      });

      const blob = await pdf(Doc).toBlob();
      
      // Download file
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `script-info-${selectedMarket}-${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Success notification
      notification.success({
        key: loadingKey,
        message: "PDF exported successfully",
        description: `${processedData.length} scripts exported`,
      });
    } catch (e) {
      notification.error({
        key: loadingKey,
        message: "Failed to export PDF",
        description: e.message || "An error occurred during export",
      });
    }
  };

  // Debounce search input
  useDebounce(
    () => {
      setDebouncedSearch(searchValue);
    },
    300,
    [searchValue]
  );

  // Error handling
  useEffect(() => {
    if (error) {
      notification.error({
        message: "Error",
        description: error.message,
      });
    }
  }, [error]);

  // Monitor pagination state changes
  useEffect(() => {
    console.log("🔄 Pagination state changed:", {
      currentPage,
      pageSize,
      totalData: processedData.length,
      totalPages: Math.ceil(processedData.length / pageSize)
    });
  }, [currentPage, pageSize, processedData.length]);



  return (
    <div style={{ padding: "24px", backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Card style={{ borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={3} style={{ margin: 0, color: "#1890ff" }}>
                Script Information
              </Title>
              <Text type="secondary">
                View script details with open prices and lot sizes from bar data
              </Text>
            </Col>
            <Col>
              <Space>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "csv",
                        label: "Export to CSV",
                        icon: <FileExcelOutlined />,
                        onClick: exportToCSV,
                      },
                      {
                        key: "json",
                        label: "Export to JSON",
                        icon: <FileTextOutlined />,
                        onClick: exportToJSON,
                      },
                      {
                        key: "pdf",
                        label: "Export to PDF",
                        icon: <FilePdfOutlined />,
                        onClick: exportToPDF,
                      },
                    ],
                  }}
                  placement="bottomRight"
                >
                  <Button
                    icon={<DownloadOutlined />}
                    style={{ borderRadius: "6px" }}
                  >
                    Export Data
                  </Button>
                </Dropdown>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                  loading={isLoading}
                  style={{ borderRadius: "6px" }}
                >
                  Refresh
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {/* Filters */}
        <div style={{ marginBottom: "24px" }}>
          <Row gutter={16} align="middle">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Text strong style={{ display: "block", marginBottom: "8px" }}>
                Market
              </Text>
              <Select
                value={selectedMarket}
                onChange={handleMarketChange}
                style={{ width: "100%" }}
                placeholder="Select Market"
              >
                {markets.map((market) => (
                  <Option key={market.value} value={market.value}>
                    {market.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Text strong style={{ display: "block", marginBottom: "8px" }}>
                Search Scripts
              </Text>
              <Input
                placeholder="Search by script name, symbol, or type..."
                prefix={<SearchOutlined />}
                value={searchValue}
                onChange={handleSearch}
                style={{ width: "100%" }}
                allowClear
              />
            </Col>
            
          </Row>
        </div>

        {/* Table */}
        <div style={{ position: "relative" }}>
          {isLoading && (
            <div style={{ 
              position: "absolute", 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              backgroundColor: "rgba(255,255,255,0.8)", 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center",
              zIndex: 1,
              borderRadius: "6px"
            }}>
              <Spin size="large" />
            </div>
          )}
          
          <Table
            columns={columns}
            dataSource={Array.isArray(paginatedData) ? paginatedData : []}
            rowKey={(record) => record._uniqueId || `script-${record._rowIndex || Math.random()}`}
            pagination={false}
            onChange={(pagination, filters, sorter) => {
              console.log("🔧 Table onChange triggered:", { pagination, filters, sorter });
              // Only handle sorting, not pagination
              if (sorter && (sorter.field !== sortConfig.field || sorter.order !== sortConfig.order)) {
                setSortConfig({
                  field: sorter.field,
                  order: sorter.order === "ascend" ? "asc" : sorter.order === "descend" ? "desc" : null,
                });
                setCurrentPage(1);
              }
            }}
            scroll={{ x: 800 }}
            size="middle"
            bordered
            style={{ borderRadius: "6px" }}
            loading={false} // We handle loading manually
          />
          
          {/* Standalone Pagination - Same approach as FooterView */}
          {processedData.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
              <Pagination
                total={processedData.length}
                current={currentPage}
                pageSize={pageSize}
                showSizeChanger
                pageSizeOptions={[10, 20, 50, 100]}
                showQuickJumper
                onChange={(page) => {
                  console.log("🔧 Standalone pagination onChange:", { page, currentPage, total: processedData.length });
                  setCurrentPage(page);
                }}
                onShowSizeChange={(_, size) => {
                  console.log("🔧 Standalone pagination onShowSizeChange:", { size, currentPageSize: pageSize });
                  setPageSize(size);
                  setCurrentPage(1);
                }}
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
              />
            </div>
          )}
        </div>



        
      </Card>
    </div>
  );
};

export default ScriptInfo;
